import React,{useEffect,useMemo,useState}from"react";
import{Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,FormControl,InputLabel,MenuItem,Paper,Select,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,TextField,Typography}from"@mui/material";
import{useNavigate}from"react-router-dom";
import Sidebar from"../AdminPanel/Sidebar";
import TopBar from"../AdminPanel/TopBar";
import{useDashboardTheme}from"../../../context/DashboardThemeContext";
import{useAuth}from"../../../context/AuthContext";
import{useAlert}from"../../../context/AlertContext";

const API_BASE="/api/purchases";

function getToken(){
  return localStorage.getItem("accessToken")||localStorage.getItem("access")||"";
}

function userCan(user,code){
  if(!user)return false;
  if(user.role==="super_admin"||user.role==="admin")return true;
  return(user.permissions||[]).includes(code);
}

export default function PurchaseCategories(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{user}=useAuth();
  const{showError,showSuccess,showInfo}=useAlert();
  const navigate=useNavigate();
  const token=getToken();

  const[empresaId,setEmpresaId]=useState("");
  const[categories,setCategories]=useState([]);
  const[loading,setLoading]=useState(false);
  const[saving,setSaving]=useState(false);

  const[modalOpen,setModalOpen]=useState(false);
  const[modalType,setModalType]=useState("CATEGORY");
  const[editing,setEditing]=useState(null);
  const[parentSelected,setParentSelected]=useState(null);
  const[name,setName]=useState("");
  const[active,setActive]=useState(true);

  const[deleteDialog,setDeleteDialog]=useState(false);
  const[categoryToDelete,setCategoryToDelete]=useState(null);
  const[deleting,setDeleting]=useState(false);

  const canCreate=userCan(user,"compras.create");
  const canEdit=userCan(user,"compras.edit");
  const canDelete=userCan(user,"compras.delete");

  const mainCategories=useMemo(
    ()=>categories.filter(item=>!item.padre_id),
    [categories]
  );

  const orderedCategories=useMemo(()=>{
    const result=[];
    mainCategories.forEach(parent=>{
      result.push(parent);
      categories
        .filter(item=>item.padre_id===parent.id)
        .sort((a,b)=>String(a.nombre||"").localeCompare(String(b.nombre||"")))
        .forEach(child=>result.push(child));
    });

    categories
      .filter(item=>item.padre_id&&!mainCategories.some(parent=>parent.id===item.padre_id))
      .forEach(item=>result.push(item));

    return result;
  },[categories,mainCategories]);

  const parseError=data=>{
    const error=data?.error||data?.detail||data?.mensaje;
    if(!error)return"No se pudo procesar la solicitud.";
    if(typeof error==="string")return error;
    if(typeof error==="object")return Object.values(error).flat().join(" ");
    return"No se pudo procesar la solicitud.";
  };

  const loadEmpresa=async()=>{
    try{
      const response=await fetch("/api/enterprise/empresa/obtener/",{
        headers:{Authorization:`Bearer ${token}`}
      });

      const data=await response.json();

      if(!response.ok){
        showError(data.error||data.detail||"No se pudo cargar la empresa.");
        return;
      }

      if(!data?.empresa?.id){
        showError("No existe una empresa configurada.");
        return;
      }

      setEmpresaId(data.empresa.id);
    }catch{
      showError("Error de conexión al cargar la empresa.");
    }
  };

  const loadCategories=async()=>{
    if(!empresaId)return;

    try{
      setLoading(true);

      const response=await fetch(
        `${API_BASE}/categorias/?empresa_id=${empresaId}`,
        {headers:{Authorization:`Bearer ${token}`}}
      );

      const data=await response.json();

      if(!response.ok){
        showError(parseError(data));
        setCategories([]);
        return;
      }

      setCategories(data.results||[]);
    }catch{
      setCategories([]);
      showError("Error de conexión.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadEmpresa();
  },[]);

  useEffect(()=>{
    loadCategories();
  },[empresaId]);

  const closeModal=()=>{
    if(saving)return;
    setModalOpen(false);
    setEditing(null);
    setParentSelected(null);
    setName("");
    setActive(true);
  };

  const openCategory=()=>{
    setEditing(null);
    setParentSelected(null);
    setModalType("CATEGORY");
    setName("");
    setActive(true);
    setModalOpen(true);
  };

  const openSubcategory=parent=>{
    setEditing(null);
    setParentSelected(parent);
    setModalType("SUBCATEGORY");
    setName("");
    setActive(true);
    setModalOpen(true);
  };

  const openEdit=category=>{
    setEditing(category);
    setName(category.nombre||"");
    setActive(Boolean(category.activo));

    if(category.padre_id){
      setModalType("SUBCATEGORY");
      setParentSelected(
        mainCategories.find(item=>item.id===category.padre_id)||null
      );
    }else{
      setModalType("CATEGORY");
      setParentSelected(null);
    }

    setModalOpen(true);
  };

  const saveCategory=async()=>{
    if(!name.trim()){
      showInfo(
        modalType==="SUBCATEGORY"
          ?"Debe indicar el nombre de la subcategoría."
          :"Debe indicar el nombre de la categoría."
      );
      return;
    }

    if(modalType==="SUBCATEGORY"&&!parentSelected){
      showInfo("Debe seleccionar la categoría a la que pertenece.");
      return;
    }

    try{
      setSaving(true);

      const body={
        empresa_id:empresaId,
        nombre:name.trim(),
        descripcion:"",
        padre_id:modalType==="SUBCATEGORY"?parentSelected?.id||null:null
      };

      if(editing)body.activo=active;

      const response=await fetch(
        editing
          ?`${API_BASE}/categorias/${editing.id}/`
          :`${API_BASE}/categorias/crear/`,
        {
          method:editing?"PUT":"POST",
          headers:{
            Authorization:`Bearer ${token}`,
            "Content-Type":"application/json"
          },
          body:JSON.stringify(body)
        }
      );

      const data=await response.json();

      if(!response.ok){
        showError(parseError(data));
        return;
      }

      showSuccess(
        data.mensaje||
        (modalType==="SUBCATEGORY"
          ?"Subcategoría guardada correctamente."
          :"Categoría guardada correctamente.")
      );

      closeModal();
      loadCategories();
    }catch{
      showError("Error de conexión.");
    }finally{
      setSaving(false);
    }
  };

  const askDelete=category=>{
    setCategoryToDelete(category);
    setDeleteDialog(true);
  };

  const closeDeleteDialog=()=>{
    if(deleting)return;
    setDeleteDialog(false);
    setCategoryToDelete(null);
  };

  const deleteCategory=async()=>{
    if(!categoryToDelete)return;

    try{
      setDeleting(true);

      const response=await fetch(
        `${API_BASE}/categorias/${categoryToDelete.id}/eliminar/?empresa_id=${empresaId}`,
        {
          method:"DELETE",
          headers:{Authorization:`Bearer ${token}`}
        }
      );

      const data=await response.json();

      if(!response.ok){
        showError(parseError(data));
        return;
      }

      showSuccess(data.mensaje||"Categoría eliminada correctamente.");
      setDeleteDialog(false);
      setCategoryToDelete(null);
      loadCategories();
    }catch{
      showError("Error de conexión.");
    }finally{
      setDeleting(false);
    }
  };

  const outlinedButtonSx={
    minHeight:40,
    px:2.4,
    borderRadius:"12px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  const smallOutlinedSx={
    minHeight:32,
    px:1.4,
    borderRadius:"8px",
    textTransform:"none",
    fontWeight:600,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none"
  };

  const smallContainedSx={
    ...dashboardTheme.buttonStyles.contained,
    minHeight:32,
    px:1.4,
    borderRadius:"8px",
    textTransform:"none",
    fontWeight:600,
    boxShadow:"none"
  };

  const dangerTextSx={
    minHeight:32,
    px:1.2,
    borderRadius:"8px",
    textTransform:"none",
    fontWeight:600,
    color:"#D32F2F",
    "&:hover":{
      bgcolor:"rgba(211,47,47,.08)"
    }
  };

  const selectMenuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        mt:.5,
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none"
      }
    }
  };

  return(
    <Box sx={{
      minHeight:"100vh",
      bgcolor:dashboardTheme.dashboard_background,
      display:"flex",
      color:dashboardTheme.text_color,
      overflowX:"hidden"
    }}>
      <Sidebar
        open={sidebarOpen}
        onClose={()=>setSidebarOpen(false)}
      />

      <Box sx={{
        flex:1,
        minWidth:0,
        minHeight:"100vh",
        display:"flex",
        flexDirection:"column"
      }}>
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}
        />

        <Box
          component="main"
          sx={{
            flex:1,
            width:"100%",
            p:{xs:1.5,sm:2,md:3}
          }}
        >
          <Box sx={{
            width:{xs:"100%",lg:"95%"},
            mx:"auto",
            mt:{xs:1,md:1.5}
          }}>
            <Box sx={{
              display:"flex",
              alignItems:{xs:"stretch",sm:"center"},
              justifyContent:"space-between",
              flexDirection:{xs:"column",sm:"row"},
              gap:1.5,
              mb:2
            }}>
              <Typography sx={{
                fontSize:{xs:24,sm:26,md:30},
                fontWeight:600,
                color:dashboardTheme.primary_color,
                lineHeight:1.2
              }}>
                Categorías de gastos
              </Typography>

              <Box sx={{
                display:"flex",
                gap:1,
                flexDirection:{xs:"column",sm:"row"}
              }}>
                <Button
                  variant="outlined"
                  onClick={()=>navigate("/admin-panel/compras")}
                  sx={outlinedButtonSx}
                >
                  Regresar
                </Button>

                {canCreate&&(
                  <Button
                    variant="contained"
                    onClick={openCategory}
                    sx={dashboardTheme.buttonStyles.contained}
                  >
                    Nueva categoría
                  </Button>
                )}
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={dashboardTheme.tableStyles.wrapper}
            >
              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table
                    size="small"
                    sx={{
                      ...dashboardTheme.tableStyles.table,
                      minWidth:850
                    }}
                  >
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell
                          sx={{
                            ...dashboardTheme.tableStyles.headCell,
                            minWidth:250
                          }}
                        >
                          Nombre
                        </TableCell>

                        <TableCell
                          sx={{
                            ...dashboardTheme.tableStyles.headCell,
                            minWidth:150
                          }}
                        >
                          Tipo
                        </TableCell>

                        <TableCell
                          sx={{
                            ...dashboardTheme.tableStyles.headCell,
                            minWidth:220
                          }}
                        >
                          Pertenece a
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Estado
                        </TableCell>

                        {(canCreate||canEdit||canDelete)&&(
                          <TableCell
                            align="right"
                            sx={{
                              ...dashboardTheme.tableStyles.headCell,
                              minWidth:310
                            }}
                          >
                            Acciones
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {orderedCategories.map(category=>{
                        const isSubcategory=Boolean(category.padre_id);

                        return(
                          <TableRow
                            key={category.id}
                            hover
                            sx={dashboardTheme.tableStyles.bodyRow}
                          >
                            <TableCell>
                              <Typography sx={{
                                fontSize:14,
                                fontWeight:isSubcategory?500:700,
                                color:dashboardTheme.text_color,
                                pl:isSubcategory?2:0
                              }}>
                                {category.nombre}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography sx={{
                                fontSize:13.5,
                                fontWeight:600,
                                color:dashboardTheme.text_color
                              }}>
                                {isSubcategory?"Subcategoría":"Categoría"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              {category.padre||"-"}
                            </TableCell>

                            <TableCell>
                              <Typography sx={{
                                fontSize:13.5,
                                fontWeight:600,
                                color:category.activo
                                  ?dashboardTheme.text_color
                                  :"#D32F2F"
                              }}>
                                {category.activo?"Activa":"Inactiva"}
                              </Typography>
                            </TableCell>

                            {(canCreate||canEdit||canDelete)&&(
                              <TableCell align="right">
                                <Box sx={{
                                  display:"flex",
                                  justifyContent:"flex-end",
                                  alignItems:"center",
                                  gap:.7,
                                  flexWrap:"wrap"
                                }}>
                                  {!isSubcategory&&canCreate&&(
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={()=>openSubcategory(category)}
                                      sx={smallContainedSx}
                                    >
                                      Agregar subcategoría
                                    </Button>
                                  )}

                                  {canEdit&&(
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={()=>openEdit(category)}
                                      sx={smallOutlinedSx}
                                    >
                                      Editar
                                    </Button>
                                  )}

                                  {canDelete&&(
                                    <Button
                                      variant="text"
                                      size="small"
                                      onClick={()=>askDelete(category)}
                                      sx={dangerTextSx}
                                    >
                                      Eliminar
                                    </Button>
                                  )}
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}

                      {!loading&&categories.length===0&&(
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            No hay categorías registradas
                          </TableCell>
                        </TableRow>
                      )}

                      {loading&&(
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            Cargando categorías...
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={modalOpen}
        onClose={closeModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          elevation:0,
          sx:{
            bgcolor:dashboardTheme.card_background,
            color:dashboardTheme.text_color,
            border:`1px solid ${dashboardTheme.border_color}`,
            borderRadius:"14px",
            boxShadow:"none"
          }
        }}
      >
        <DialogTitle sx={{
          fontWeight:700,
          color:dashboardTheme.text_color
        }}>
          {editing
            ?modalType==="SUBCATEGORY"
              ?"Editar subcategoría"
              :"Editar categoría"
            :modalType==="SUBCATEGORY"
              ?"Nueva subcategoría"
              :"Nueva categoría"}
        </DialogTitle>

        <DialogContent>
          {modalType==="SUBCATEGORY"&&(
            editing?(
              <FormControl
                fullWidth
                size="small"
                sx={{
                  mt:1,
                  mb:1.5,
                  ...dashboardTheme.inputStyles
                }}
              >
                <InputLabel>Pertenece a</InputLabel>

                <Select
                  label="Pertenece a"
                  value={parentSelected?.id||""}
                  onChange={e=>{
                    setParentSelected(
                      mainCategories.find(
                        item=>item.id===e.target.value
                      )||null
                    );
                  }}
                  MenuProps={selectMenuProps}
                >
                  {mainCategories
                    .filter(item=>item.id!==editing?.id)
                    .map(category=>(
                      <MenuItem
                        key={category.id}
                        value={category.id}
                      >
                        {category.nombre}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            ):(
              <Box sx={{
                mt:1,
                mb:1.5,
                p:1.5,
                border:`1px solid ${dashboardTheme.border_color}`,
                borderRadius:"10px",
                bgcolor:dashboardTheme.dashboard_background
              }}>
                <Typography sx={{
                  fontSize:12.5,
                  color:dashboardTheme.muted_text_color
                }}>
                  Pertenece a
                </Typography>

                <Typography sx={{
                  fontSize:15,
                  fontWeight:700,
                  color:dashboardTheme.text_color
                }}>
                  {parentSelected?.nombre||"-"}
                </Typography>
              </Box>
            )
          )}

          <TextField
            autoFocus
            fullWidth
            size="small"
            label={
              modalType==="SUBCATEGORY"
                ?"Nombre de la subcategoría"
                :"Nombre de la categoría"
            }
            placeholder={
              modalType==="SUBCATEGORY"
                ?"Ej. Internet"
                :"Ej. Servicios básicos"
            }
            value={name}
            onChange={e=>setName(e.target.value)}
            onKeyDown={e=>{
              if(e.key==="Enter"&&!saving)saveCategory();
            }}
            sx={{
              mt:modalType==="CATEGORY"?1:0,
              ...dashboardTheme.inputStyles
            }}
          />

          {editing&&(
            <FormControl
              fullWidth
              size="small"
              sx={{
                mt:1.5,
                ...dashboardTheme.inputStyles
              }}
            >
              <InputLabel>Estado</InputLabel>

              <Select
                label="Estado"
                value={active?"activo":"inactivo"}
                onChange={e=>setActive(e.target.value==="activo")}
                MenuProps={selectMenuProps}
              >
                <MenuItem value="activo">Activa</MenuItem>
                <MenuItem value="inactivo">Inactiva</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>

        <DialogActions sx={{p:2,pt:1}}>
          <Button
            variant="outlined"
            onClick={closeModal}
            disabled={saving}
            sx={outlinedButtonSx}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={saveCategory}
            disabled={saving}
            sx={dashboardTheme.buttonStyles.contained}
          >
            {saving?"Guardando...":"Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          elevation:0,
          sx:{
            bgcolor:dashboardTheme.card_background,
            color:dashboardTheme.text_color,
            border:`1px solid ${dashboardTheme.border_color}`,
            borderRadius:"14px",
            boxShadow:"none"
          }
        }}
      >
        <DialogTitle sx={{
          fontWeight:700,
          color:dashboardTheme.text_color
        }}>
          Eliminar {categoryToDelete?.padre_id?"subcategoría":"categoría"}
        </DialogTitle>

        <DialogContent>
          <Typography sx={{
            fontSize:14,
            color:dashboardTheme.text_color
          }}>
            ¿Deseas eliminar <strong>{categoryToDelete?.nombre}</strong>?
          </Typography>

          <Typography sx={{
            fontSize:12.5,
            color:dashboardTheme.muted_text_color,
            mt:1
          }}>
            Si tiene registros relacionados, el sistema la desactivará en lugar de eliminarla.
          </Typography>
        </DialogContent>

        <DialogActions sx={{p:2,pt:1}}>
          <Button
            variant="outlined"
            onClick={closeDeleteDialog}
            disabled={deleting}
            sx={outlinedButtonSx}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={deleteCategory}
            disabled={deleting}
            sx={{
              minHeight:40,
              px:2.4,
              borderRadius:"12px",
              textTransform:"none",
              fontWeight:700,
              color:"#fff",
              bgcolor:"#D32F2F",
              boxShadow:"none",
              "&:hover":{
                bgcolor:"#B71C1C",
                boxShadow:"none"
              }
            }}
          >
            {deleting?"Eliminando...":"Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}